import { useContext, useState } from "react";
import styled from "styled-components";
import { Button, Radio, RadioGroup } from "@blueprintjs/core";

import { Code } from "@components";
import { Feature } from "@utils";

import { WorkbenchContext } from "@pages/editor/providers";
import { SnippetAliasAddress } from "@pages/editor/stores";

interface Props {
  closeDialog: () => void;
}

const DialogContainer = styled.div`
  padding: 40px;
  padding-bottom: 20px;
  max-width: 600px;
  font-size: var(--global-font-size-m);
`;

const StyledRadioGroup = styled(RadioGroup)`
  padding: 0 20px;
`;

const OptionDescription = styled.div`
  font-size: var(--global-font-size-s);
`;

const SmallText = styled.div`
  font-size: var(--global-font-size-xs);
`;

const Buttons = styled.div`
  display: flex;
  justify-content: right;
  gap: 8px;
`;

export const LegacyRuleSaveDialog = ({ closeDialog }: Props) => {
  const { workbench } = useContext(WorkbenchContext);
  const [selectedValue, setSelectedValue] = useState("new");
  const { bundle } = workbench;
  const isNolicy = Feature.isNolicy({ org: workbench.org });

  if (
    !bundle?.rule?.id ||
    !(workbench.address instanceof SnippetAliasAddress)
  ) {
    // should never get here, since SaveButton checks if bundle is null
    // and address must be that of a snippet
    return null;
  }

  const saveAndClose = () => {
    if (
      !bundle?.rule?.id ||
      !(workbench.address instanceof SnippetAliasAddress)
    ) {
      // Again, should never get here
      // This is just for type checking reasons
      closeDialog();
      return;
    }

    if (selectedValue === "overwrite") {
      bundle.setRuleId(workbench.address.name);
    }
    workbench.saveBundle();
    closeDialog();
  };

  return (
    <DialogContainer>
      <div>This rule is currently in a legacy format. Would you like to...</div>
      <StyledRadioGroup
        selectedValue={selectedValue}
        onChange={(e) => setSelectedValue(e.currentTarget.value)}
      >
        <Radio label={"Create new"} value={"new"}>
          <OptionDescription>
            This will create a new rule with id{" "}
            <Code inline light>
              {bundle.rule.id}
            </Code>
            <SmallText>
              Note: this newly created rule will not be added to{" "}
              {isNolicy ? "your rule board" : "any policies"}.
            </SmallText>
          </OptionDescription>
        </Radio>
        <Radio label={"Overwrite"} value={"overwrite"}>
          <OptionDescription>
            This will overwrite the current rule and change the id to{" "}
            <Code inline light>
              {workbench.address.name}
            </Code>
          </OptionDescription>
        </Radio>
      </StyledRadioGroup>
      <Buttons>
        <Button intent="primary" outlined large onClick={closeDialog}>
          Cancel
        </Button>
        <Button intent="primary" large onClick={saveAndClose}>
          Save
        </Button>
      </Buttons>
    </DialogContainer>
  );
};
